/**
 * useCapacityPause
 * ----------------
 * Generic "back off until the master backend's capacity guard
 * recovers" timer. When a request returns 429 with the body
 * `{ "reason": "capacity_rejected", "retry_after_seconds": N }`
 * (or a `Retry-After: N` header), the caller invokes
 * `pauseFor(N)` and the hook owns the rest:
 *
 *   - `isPaused`: rendered as a flag for `disabled` props on
 *     "Sync Now" buttons, and as a guard inside auto-sync /
 *     backfill loops to short-circuit before the request fires.
 *   - `remainingSeconds`: powers the "Try again in 27s" toast
 *     copy and live-counts down via a 1Hz interval that only
 *     runs while paused (zero overhead in the steady state).
 *   - `pauseFor(seconds)`: idempotent — calling twice extends to
 *     the LATER of the two windows so two simultaneous 429s on
 *     parallel calls don't shorten the back-off.
 *   - `clear()`: optimistic recovery. Callers should fire this
 *     the moment the next request succeeds, so the user isn't
 *     stuck waiting out a stale window once the master is happy.
 *
 * Why a hook (not just a useState):
 *   - Centralises the floor (default 30s — the master's capacity
 *     guard cool-down can be 0 from the body if the rejection was
 *     "transient backpressure rather than hard cooldown", and the
 *     last thing we want is a 0-second back-off that immediately
 *     re-fires).
 *   - Centralises the ceiling (10 min — never paint ourselves
 *     into a corner if the master returns a malformed huge
 *     retry_after_seconds).
 *   - Owns the ticking interval, including teardown.
 *   - Exposes a ref so render-loops outside React's render cycle
 *     (setInterval body, useEmailBackfill walker) can read the
 *     latest value without subscribing to re-renders.
 *
 * NOT in scope here:
 *   - Reading the 429 response itself — callers do that via
 *     `QuotechApiError` (status / code / retryAfterSeconds).
 *   - Cross-tab coordination — each tab owns its own pause; if
 *     the user has 3 tabs open and 1 hits a 429, the other 2
 *     will hit it shortly too and pause themselves. Worth
 *     revisiting if we move to a SharedWorker for sync.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

const DEFAULT_FLOOR_SECONDS = 30
const CEILING_SECONDS = 10 * 60

// BUG-2026-07-17-EMAIL-SYNC-429-STORM — the pause window must survive
// the component that engaged it. The pause used to live only in the
// mounting component's useState, so a route change, an org-switch
// remount, or a full page open reset it to "not paused" and the mount
// auto-sync re-fired into a master that had just said 429 — the
// operator log showed repeated capacity 429s per page open. The
// paused-until epoch is now mirrored in sessionStorage (per tab,
// matching the documented per-tab scope) so every new mount/reload in
// the same tab honours the window it was already given.
const STORAGE_KEY = 'qt_capacity_paused_until'

function readPersistedPause(): number | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const ts = Number(raw)
    if (!Number.isFinite(ts) || ts <= Date.now()) return null
    return ts
  } catch {
    return null
  }
}

function persistPause(until: number | null): void {
  try {
    if (until === null) sessionStorage.removeItem(STORAGE_KEY)
    else sessionStorage.setItem(STORAGE_KEY, String(until))
  } catch {
    // Storage unavailable — pause degrades to per-mount (old behaviour).
  }
}

export interface CapacityPauseApi {
  isPaused: boolean
  remainingSeconds: number
  pauseFor: (seconds: number | null | undefined) => void
  clear: () => void
  /**
   * Refs for use inside callbacks / setInterval bodies that run
   * outside the React render cycle and need the *current* value
   * without re-subscribing.
   */
  isPausedRef: React.MutableRefObject<boolean>
  pausedUntilRef: React.MutableRefObject<number | null>
}

export function useCapacityPause(opts?: {
  floorSeconds?: number
  ceilingSeconds?: number
}): CapacityPauseApi {
  const floor = opts?.floorSeconds ?? DEFAULT_FLOOR_SECONDS
  const ceiling = opts?.ceilingSeconds ?? CEILING_SECONDS

  const [pausedUntil, setPausedUntilState] = useState<number | null>(
    readPersistedPause,
  )
  const setPausedUntil = useCallback(
    (next: number | null | ((prev: number | null) => number | null)) => {
      setPausedUntilState((prev) => {
        const value = typeof next === 'function' ? next(prev) : next
        persistPause(value)
        return value
      })
    },
    [],
  )
  const [, forceTick] = useState(0)

  const pausedUntilRef = useRef<number | null>(null)
  pausedUntilRef.current = pausedUntil
  const isPausedRef = useRef(false)

  const isPaused = pausedUntil !== null && Date.now() < pausedUntil
  isPausedRef.current = isPaused

  const remainingSeconds = isPaused
    ? Math.max(0, Math.ceil(((pausedUntil as number) - Date.now()) / 1000))
    : 0

  const pauseFor = useCallback(
    (seconds: number | null | undefined) => {
      const requested =
        typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0
          ? seconds
          : floor
      const clamped = Math.min(ceiling, Math.max(floor, requested))
      const next = Date.now() + clamped * 1000
      setPausedUntil((prev) => {
        // If a longer window is already in effect, don't shorten it.
        if (prev !== null && prev > next) return prev
        return next
      })
    },
    [floor, ceiling, setPausedUntil],
  )

  const clear = useCallback(() => {
    setPausedUntil(null)
  }, [setPausedUntil])

  // Tick at 1 Hz while paused so the countdown re-renders. The
  // interval clears itself the moment the window elapses so we're
  // not burning CPU when there's nothing to update.
  useEffect(() => {
    if (pausedUntil === null) return
    const id = window.setInterval(() => {
      if (Date.now() >= pausedUntil) {
        setPausedUntil(null)
        return
      }
      forceTick((n) => (n + 1) % 1_000_000)
    }, 1000)
    return () => window.clearInterval(id)
  }, [pausedUntil, setPausedUntil])

  return {
    isPaused,
    remainingSeconds,
    pauseFor,
    clear,
    isPausedRef,
    pausedUntilRef,
  }
}
